import { Fragment, ChangeEvent, FocusEvent, useState } from 'react';
import { EventWithDataHandler } from 'redux-form';
import connect from 'components/Shared/connect';

import {
  addressSelected,
  deliveryAddressChanged,
  onBlurCheckout
} from 'views/CheckoutPage/actions';
import {
  fieldStreet,
  fieldStreetAlternate,
  fieldZipcode,
  fieldZipcodeAlternate,
  getAddressForDeliveryInformation,
  getCustomerType
} from 'views/CheckoutPage/selector';
import { SuggestedAddress } from 'models/SuggestedAddress';
import DeliveryForm from './deliveryForm';
import { useSelector } from 'react-redux';
import { CustomerType } from 'models/CustomerType';
import { selectNameMaxLength } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/selector';

interface DispatchProps {
  city: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  street: string;
}

export interface RenderFormProps {
  hasDeliveryAddress: boolean;
  suggestionSelected: (field: string) => (item: SuggestedAddress) => void;
  deliveryAddressChanged: EventWithDataHandler<FocusEvent>;
  onBlur: EventWithDataHandler<FocusEvent>;
  hasValidAddress: boolean;
  hasValidZipCode: boolean;
  resetAddressValidation: () => void;
  customerType: CustomerType;
  maxNameLength?: number;
}

type FormType = 'primary' | 'delivery';

interface OwnProps {
  renderForm: (props: RenderFormProps) => JSX.Element;
  formType?: FormType;
}
interface MergedProps extends OwnProps {
  hasDeliveryAddress: boolean;
  city: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  street: string;
  deliveryAddressChanged: EventWithDataHandler<FocusEvent>;
  onBlur: EventWithDataHandler<FocusEvent>;
  suggestionSelected: (field: string) => (item: SuggestedAddress) => void;
}

const ManualForm = ({
  onBlur,
  hasDeliveryAddress,
  renderForm,
  suggestionSelected,
  deliveryAddressChanged,
  formType
}: MergedProps) => {
  const [hasValidAddress, setHasValidAddress] = useState(false);
  const [hasValidZipCode, setHasValidZipCode] = useState(false);
  const customerType = useSelector(getCustomerType);
  const maxNameLength = useSelector(selectNameMaxLength);

  const decoratedSuggestionSelected = (field: string) => {
    const cb = suggestionSelected(field);
    return (item: SuggestedAddress) => {
      cb(item);

      if (field === fieldStreet || field === fieldStreetAlternate) {
        setHasValidAddress(true);
      } else if (field === fieldZipcode || field === fieldZipcodeAlternate) {
        setHasValidZipCode(true);
      }
    };
  };

  const resetAddressValidation = () => {
    setHasValidZipCode(false);
    setHasValidAddress(false);
  };

  const renderFormProps: RenderFormProps = {
    hasDeliveryAddress,
    suggestionSelected: decoratedSuggestionSelected,
    deliveryAddressChanged,
    onBlur,
    hasValidAddress,
    hasValidZipCode,
    resetAddressValidation,
    customerType,
    maxNameLength
  };

  return (
    <Fragment>
      {renderForm(renderFormProps)}
      {hasDeliveryAddress && formType === 'primary' && <DeliveryForm customerType={customerType} />}
    </Fragment>
  );
};

export default connect<MergedProps & DispatchProps, OwnProps>(
  (state, ownProps) => ({
    ...getAddressForDeliveryInformation({
      state,
      useDeliveryAddress: ownProps.formType === 'delivery'
    })
  }),
  dispatch => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur: (_e?: FocusEvent, n?: any, _p?: any, name?: string) =>
      dispatch(onBlurCheckout(name, n)),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deliveryAddressChanged: (_e?: ChangeEvent, n?: any, _p?: any, name?: string) =>
      dispatch(deliveryAddressChanged(n, name)),
    suggestionSelected: (field: string) => (item: SuggestedAddress) =>
      dispatch(addressSelected(item, field))
  })
)(ManualForm);
